import { watch } from "vue";

import { setUnauthorizedHandler } from "@/api/http";
import { resourceCache } from "@/cache/resourceCache";
import { resetSessionState } from "@/stores/sessionReset";
import { useUserStore } from "@/stores/user";

export function installSessionLifecycle(
  shouldResolveSession: () => boolean = () => true,
): () => void {
  const userStore = useUserStore();
  setUnauthorizedHandler(() => {
    void userStore.confirmUnauthorized();
  });

  const stopWatchingSessionRequirement = watch(
    shouldResolveSession,
    (required) => {
      if (required) void userStore.ensureSession().catch(() => undefined);
    },
    { flush: "sync", immediate: true },
  );

  const stopWatchingSession = watch(
    () => [userStore.loginState, userStore.user?.id ?? null] as const,
    ([loginState, userId], [previousLoginState, previousUserId]) => {
      const leftAuthenticatedUser =
        previousLoginState === "authenticated" &&
        previousUserId !== null &&
        (loginState !== "authenticated" || userId !== previousUserId);
      if (!leftAuthenticatedUser) return;
      resourceCache.invalidate(previousUserId);
      resetSessionState();
    },
    { flush: "sync" },
  );

  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    stopWatchingSessionRequirement();
    stopWatchingSession();
    setUnauthorizedHandler(null);
  };
}
